import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Stack, Drawer, Button, Typography, ListItemButton, Badge, Collapse, IconButton } from '@mui/material';
import { alpha, useTheme, styled } from '@mui/material/styles';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';

import Scrollbar from 'src/components/scrollbar';
import Iconify from 'src/components/iconify';

import { NAV } from './config-layout';
import useNavConfig from './config-navigation';

// ----------------------------------------------------------------------

const StyledBadge = styled(Badge)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  '& .MuiBadge-badge': {
    backgroundColor: 'rgba(0, 184, 217, 0.16)',
    color: '#00B8D9',
    minWidth: 22,
    height: 22,
    padding: '0 8px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: '6px',
    textTransform: 'none',
    transition: theme.transitions.create(['background-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: (theme) => alpha(theme.palette.info.main, 0.24),
      boxShadow: `0 0 0 1px ${alpha(theme.palette.info.main, 0.24)}`,
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: '0.2px',
    boxShadow: `0 0 0 1px ${alpha(theme.palette.info.main, 0.16)}`,
  },
}));

export default function Nav({ openNav, onCloseNav }) {
  const pathname = usePathname();
  const theme = useTheme();
  const upLg = useResponsive('up', 'lg');
  const navConfig = useNavConfig();
  const [collapsedSections, setCollapsedSections] = useState({
    'Advanced Tools': true, // Default collapsed
  });

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderLogo = (
    <Box 
      sx={{ 
        px: 2.5, 
        py: 2, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backgroundColor: (theme) => alpha(theme.palette.background.neutral, 0.3),
        backdropFilter: 'blur(8px)'
      }}
    >
      <Box 
        component="div" 
        sx={{ 
          width: 32, 
          height: 32, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1,
          borderRadius: '50%',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          backgroundImage: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`,
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Box 
          component="img" 
                      src="/favicon/favicon.png" 
                          alt="Aisy Logo" 
          sx={{ 
            width: 24, 
            height: 24,
          }}
        />
      </Box>
    </Box>
  );

  const renderMenu = (
    <Stack
      component="nav"
      spacing={0.5}
      sx={{
        px: 2,
        width: '100%',
        position: 'relative',
        zIndex: 1,
        pt: 2,
        '& > .MuiTypography-overline': {
          mt: 3,
          mb: 1,
          position: 'relative',
          '&:after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '1px',
            background: (theme) => `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.5)}, ${alpha(theme.palette.divider, 0.1)})`,
          }
        },
        '& > .MuiTypography-overline:first-of-type': {
          mt: 2,
        },
        '& > .MuiListItemButton-root': {
          transition: (theme) => theme.transitions.create(['padding-left', 'padding-right', 'background-color', 'transform'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            px: 2.5,
            transform: 'translateX(4px)',
          }
        }
      }}
    >
      {navConfig.map((item, index) => {
        if (item.type === 'section' && item.collapsible) {
          const isCollapsed = collapsedSections[item.title];
          const nextItems = [];
          
          // Collect items that belong to this section
          for (let i = index + 1; i < navConfig.length; i++) {
            const nextItem = navConfig[i];
            if (nextItem.type === 'section') break;
            if (nextItem.advanced) nextItems.push(nextItem);
          }
          
          return (
            <Box key={item.title}>
              <CollapsibleSection 
                item={item} 
                isCollapsed={isCollapsed}
                onToggle={() => setCollapsedSections(prev => ({
                  ...prev,
                  [item.title]: !prev[item.title]
                }))}
              />
              <Collapse in={!isCollapsed}>
                <Box sx={{ pl: 1 }}>
                  {nextItems.map((subItem) => {
                    if (subItem.type === 'subsection') {
                      return (
                        <SubsectionHeader key={subItem.title} title={subItem.title} />
                      );
                    }
                    return (
                      <NavItem 
                        key={subItem.title} 
                        item={subItem} 
                        isSubsectionItem={!!subItem.subsection}
                      />
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        }
        
        // Skip advanced items as they're handled by collapsible sections
        if (item.advanced || item.type === 'subsection') return null;
        
        return <NavItem key={item.title} item={item} />;
      })}
    </Stack>
  );
  const renderUpgrade = (
    <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <Button
          href="https://app.prefect.cloud/account/dd54b555-c362-40b6-911a-71836fe15360/workspace/7f1eed7f-7391-4981-96cf-20ebfd4eb710/dashboard/"
          target="_blank"
          variant="contained"
          color="primary"
          sx={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            width: '100%',
            borderRadius: 1.5,
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              transform: 'translateY(-2px)',
            },
            transition: (theme) => theme.transitions.create(['box-shadow', 'transform'], {
              duration: theme.transitions.duration.shorter
            }),
          }}
        >
          Monitor Backend Jobs
        </Button>
    </Box>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {renderLogo}
      {renderMenu}

      <Box sx={{ flexGrow: 1 }} />

      {renderUpgrade}
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {upLg ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.WIDTH,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
            boxShadow: '4px 0 24px 0 rgba(0,0,0,0.1)',
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
              bgcolor: 'background.default',
              background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

// ----------------------------------------------------------------------

const CollapsibleSection = ({ item, isCollapsed, onToggle }) => {
  const theme = useTheme();
  
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2.5,
        py: 1.5,
        cursor: 'pointer',
        borderRadius: 0.75,
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.shorter
        }),
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }
      }}
    >
      <Typography 
        variant="overline" 
        sx={{ 
          color: 'primary.main',
          opacity: 0.9,
          fontWeight: 'bold',
          letterSpacing: '0.08em',
          flexGrow: 1,
        }}
      >
        {item.title}
      </Typography>
      <IconButton size="small" sx={{ ml: 1 }}>
        <Iconify 
          icon={isCollapsed ? "mdi:chevron-right" : "mdi:chevron-down"} 
          width={16}
          sx={{ color: 'primary.main' }}
        />
      </IconButton>
    </Box>
  );
};

CollapsibleSection.propTypes = {
  item: PropTypes.object.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const SubsectionHeader = ({ title }) => {
  const theme = useTheme();
  
  return (
    <Typography
      variant="caption"
      sx={{
        px: 2.5,
        py: 1,
        mt: 2,
        mb: 0.5,
        fontSize: '0.7rem',
        fontWeight: 'bold',
        color: alpha(theme.palette.text.secondary, 0.8),
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        display: 'block',
      }}
    >
      {title}
    </Typography>
  );
};

SubsectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
};

const NavItem = ({ item, isSubsectionItem = false }) => {
  const pathname = usePathname();
  const theme = useTheme();
  const active = item.path === pathname;

  if (item.type === 'section') {
    return (
      <Typography 
        variant="overline" 
        sx={{ 
          px: 2.5, 
          py: 1.5, 
          color: 'primary.main',
          opacity: 0.9,
          fontWeight: 'bold',
          letterSpacing: '0.08em',
          display: 'block',
          transition: theme.transitions.create(['opacity', 'color'], {
            duration: theme.transitions.duration.shorter
          }),
          '&:hover': {
            opacity: 1,
            color: theme.palette.primary.light,
            background: 'transparent',
          }
        }}
      >
        {item.title}
      </Typography>
    );
  }

  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      sx={{
        minHeight: isSubsectionItem ? 36 : 44,
        borderRadius: 0.75,
        typography: 'body2',
        color: 'text.secondary',
        textTransform: 'capitalize',
        fontWeight: 'fontWeightMedium',
        position: 'relative',
        paddingRight: item.count > 0 ? 3 : 2.5,
        paddingLeft: isSubsectionItem ? 4 : 2.5,
        fontSize: isSubsectionItem ? '0.8rem' : '0.875rem',
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: theme.transitions.duration.shorter
        }),
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          color: 'primary.main',
          '&:before': {
            height: 12,
            opacity: 1,
          },
        },
        ...(active && {
          color: 'primary.main',
          fontWeight: 'fontWeightSemiBold',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:before': {
            height: 24,
            opacity: 1,
          },
        }),
      }}
    >
      <Box 
        component="span" 
        sx={{ 
          width: isSubsectionItem ? 20 : 24, 
          height: isSubsectionItem ? 20 : 24, 
          mr: isSubsectionItem ? 1.5 : 2,
          display: 'flex',
          alignItems: 'center',
          color: active ? 'primary.main' : 'inherit',
          transition: theme.transitions.create(['color'], {
            duration: theme.transitions.duration.shorter
          }),
          '.MuiListItemButton-root:hover &': {
            color: 'primary.main'
          }
        }}
      >
        {item.icon}
      </Box>

      <Box component="span" sx={{ flexGrow: 1 }}>{item.title}</Box>

      {item.count > 0 && (
        <StyledBadge
          badgeContent={item.count > 99 ? '99+' : item.count}
          sx={{
            position: 'absolute',
            right: 8,
            transition: 'all 0.2s ease',
          }}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    path: PropTypes.string,
    icon: PropTypes.node,
    type: PropTypes.string,
    count: PropTypes.number,
  }),
  isSubsectionItem: PropTypes.bool,
};
